import { type SxProps, type Theme } from "@mui/material";

import { MESSAGE_CUSTOM_TYPES } from "../constants";
import type { AdminChatMessage, PlacementChannelMetadata } from "../types";
import { PlacementApplicationAutoInterviewInvite } from "./PlacementApplicationAutoInterviewInvite";
import { PlacementApplicationInterviewCancelled } from "./PlacementApplicationInterviewCancelled";
import { PlacementApplicationInterviewScheduled } from "./PlacementApplicationInterviewScheduled";
import { PlacementApplicationInvite } from "./PlacementApplicationInvite";
import { PlacementApplicationMessage } from "./PlacementApplicationMessage";
import { PlacementApplicationSuggestInterview } from "./PlacementApplicationSuggestInterview";

interface PlacementMessageRouterProps {
  message: AdminChatMessage;
  channelUrl: string;
  channelMetadata?: PlacementChannelMetadata;
  sx?: SxProps<Theme>;
}

/**
 * Routes placement-related admin messages to the appropriate component.
 * Returns null if the message type is not a recognized placement message.
 */
export function PlacementMessageRouter(
  props: PlacementMessageRouterProps
): React.ReactElement | null {
  const { message, channelUrl, channelMetadata, sx } = props;

  switch (message.customType) {
    case MESSAGE_CUSTOM_TYPES.PLACEMENT_APPLICATION: {
      return (
        <PlacementApplicationMessage
          message={message}
          channelUrl={channelUrl}
          channelMetadata={channelMetadata}
          sx={sx}
        />
      );
    }
    case MESSAGE_CUSTOM_TYPES.PLACEMENT_APPLICATION_INVITE: {
      return <PlacementApplicationInvite message={message} sx={sx} />;
    }
    case MESSAGE_CUSTOM_TYPES.PLACEMENT_APPLICATION_AUTO_INTERVIEW_INVITE: {
      return (
        <PlacementApplicationAutoInterviewInvite
          message={message}
          channelUrl={channelUrl}
          channelMetadata={channelMetadata}
          sx={sx}
        />
      );
    }
    case MESSAGE_CUSTOM_TYPES.PLACEMENT_APPLICATION_SUGGEST_INTERVIEW: {
      return (
        <PlacementApplicationSuggestInterview
          message={message}
          channelUrl={channelUrl}
          channelMetadata={channelMetadata}
          sx={sx}
        />
      );
    }
    case MESSAGE_CUSTOM_TYPES.PLACEMENT_APPLICATION_CHAT_INTERVIEW_SCHEDULED: {
      return (
        <PlacementApplicationInterviewScheduled
          message={message}
          channelMetadata={channelMetadata}
          sx={sx}
        />
      );
    }
    case MESSAGE_CUSTOM_TYPES.INTERVIEW_WORKER_CANCELLATION: {
      return (
        <PlacementApplicationInterviewCancelled
          message={message}
          channelMetadata={channelMetadata}
          sx={sx}
        />
      );
    }
    default: {
      return null;
    }
  }
}
